import React, { useState } from "react";
import "./ReceiptUpload.css";
import ActionButton from "../components/ActionButton.tsx";
import { PiPlus, PiSpinnerGap } from "react-icons/pi";
import DismissableBanner from "../components/DismissableBanner.tsx";
import EditReceiptForm from "../components/EditReceiptForm.tsx";
import UploadArea from "../components/right-sidebar/UploadArea.tsx";
import { supabase } from "../SupabaseClient.tsx";
import { useNavigate } from "react-router-dom";

// Define interface for AI response
interface AiReceiptData {
  vendor: string;
  purchase_id: string;
  purchase_date: string;
  purchase_amount: number;
  item_category: string;
}

// Define interface for uploaded file info
interface UploadedFileInfo {
  id: string;
  file: File;
  order: any | null;
  previewUrl: string;
  fileName: string;
  processing: boolean;
}

const ReceiptUpload = () => {
  const navigate = useNavigate();
  // State to track uploaded files and their temporary orders
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileInfo[]>([]);
  const [isAddingToLibrary, setIsAddingToLibrary] = useState(false);

  // Handle file upload from the upload button
  const handleUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".pdf,.jpg,.jpeg,.png";

    input.onchange = (e) => {
      const selectedFiles = (e.target as HTMLInputElement).files;
      if (selectedFiles) {
        addFilesToUploadList(selectedFiles);
      }
    };

    input.click();
  };

  // Handle files from UploadReceipt component
  const handleFilesFromUploadComponent = (files: FileList | null) => {
    if (files) {
      addFilesToUploadList(files);
    }
  };

  // Process a single file with AI
  const processFileWithAI = async (fileInfo: UploadedFileInfo) => {
    try {
      // Mark this file as processing
      setUploadedFiles((prevFiles) =>
        prevFiles.map((item) =>
          item.id === fileInfo.id ? { ...item, processing: true } : item
        )
      );

      // Prepare form data
      const formData = new FormData();
      formData.append("file", fileInfo.file);

      // Call the Supabase Edge Function
      const { data, error } = await supabase.functions.invoke("process-file", {
        body: formData,
      });

      if (error) {
        console.error("Error processing file:", error);
        return;
      }

      console.log("File processed successfully:", data);

      // Create a temporary order object from AI data
      const aiData = data as AiReceiptData;

      // Create an order object, handling both valid data and empty/missing data cases
      const order = {
        id: fileInfo.id,
        name: aiData?.item_category || null,
        purchase_id: aiData?.purchase_id || null,
        purchase_date: aiData?.purchase_date || null,
        amount: aiData?.purchase_amount || null,
        origin: { name: "Manual Upload" },
        status: {
          name:
            // If we have valid AI data with required fields
            aiData && aiData.purchase_date && aiData.purchase_amount
              ? "Ready to Save"
              : // If AI returned no data or incomplete data
                "Needs Details",
        },
        original_filename: fileInfo.fileName,
        beneficiary: "Myself",
        notes: null,
      };

      // Update the file info with the new order
      setUploadedFiles((prevFiles) =>
        prevFiles.map((item) =>
          item.id === fileInfo.id ? { ...item, order, processing: false } : item
        )
      );
    } catch (error) {
      console.error("Unexpected error processing file:", error);
      // Mark as not processing even if there was an error
      setUploadedFiles((prevFiles) =>
        prevFiles.map((item) =>
          item.id === fileInfo.id ? { ...item, processing: false } : item
        )
      );
    }
  };

  // Common function to add files to the upload list
  const addFilesToUploadList = (files: FileList) => {
    const newFiles = Array.from(files).map((file) => {
      // Generate a unique ID for the file
      const id = crypto.randomUUID();

      // Create a temporary URL for the file preview
      const previewUrl = URL.createObjectURL(file);

      return {
        id,
        file,
        order: null,
        previewUrl,
        fileName: file.name,
        processing: false,
      };
    });

    setUploadedFiles((prev) => [...prev, ...newFiles]);

    // Process each new file with AI
    newFiles.forEach((fileInfo) => {
      processFileWithAI(fileInfo);
    });
  };

  // Remove a file from the upload list
  const handleRemoveFile = (id: string) => {
    setUploadedFiles((prev) => {
      const filtered = prev.filter((item) => item.id !== id);
      // Clean up object URLs
      const itemToRemove = prev.find((item) => item.id === id);
      if (itemToRemove && itemToRemove.previewUrl) {
        URL.revokeObjectURL(itemToRemove.previewUrl);
      }
      return filtered;
    });
  };

  // Save all receipt data to the database
  const handleAddToLibrary = async () => {
    try {
      setIsAddingToLibrary(true);

      // Only process files that have been fully processed by AI
      const filesToSave = uploadedFiles.filter(
        (file) => file.order && !file.processing
      );

      if (filesToSave.length === 0) {
        console.log("No ready files to save");
        setIsAddingToLibrary(false);
        return;
      }

      // Create an array of promises, one for each file to be processed
      const uploadPromises = filesToSave.map(async (fileInfo) => {
        try {
          // First upsert the order data to get an ID
          const { data, error } = await supabase
            .from("orders")
            .upsert(
              {
                name: fileInfo.order.name,
                purchase_id: fileInfo.order.purchase_id || fileInfo.id,
                purchase_date: fileInfo.order.purchase_date,
                amount: fileInfo.order.amount,
                beneficiary: fileInfo.order.beneficiary,
                notes: fileInfo.order.notes,
                original_filename: fileInfo.fileName,
                status_id: 2, // Saved status
                origin_id: 2, // Manual Upload
                receipt_captured: true,
              },
              {
                onConflict: "purchase_id, user_id, origin_id",
                ignoreDuplicates: false,
              }
            )
            .select("id, user_id")
            .single();

          if (error) {
            console.error("Error upserting order:", error);
            return { success: false, fileInfo, error };
          }

          if (data) {
            // Now that we have the order ID and user ID, create a storage path
            const storagePath = `${data.user_id}/${data.id}.pdf`;

            // Upload the file to storage
            const { error: uploadError } = await supabase.storage
              .from("order_receipts")
              .upload(storagePath, fileInfo.file, {
                contentType: fileInfo.file.type,
                upsert: true,
              });

            if (uploadError) {
              console.error("Error uploading file to storage:", uploadError);
              return { success: false, fileInfo, error: uploadError };
            }

            // Update the order with the storage path
            const { error: updateError } = await supabase
              .from("orders")
              .update({ storage_path: storagePath })
              .eq("id", data.id);

            if (updateError) {
              console.error(
                "Error updating order with storage path:",
                updateError
              );
              return { success: false, fileInfo, error: updateError };
            }

            return { success: true, fileInfo, orderId: data.id };
          }
          return {
            success: false,
            fileInfo,
            error: "No data returned from upsert",
          };
        } catch (error) {
          console.error("Error processing file:", error);
          return { success: false, fileInfo, error };
        }
      });

      // Wait for all uploads to complete
      const results = await Promise.all(uploadPromises);

      // Log results
      const successCount = results.filter((r) => r.success).length;
      console.log(
        `Successfully processed ${successCount} of ${results.length} files`
      );

      setIsAddingToLibrary(false);

      // Navigate to receipt library after upload attempt (even if some failed)
      navigate("/receipt-library");
    } catch (error) {
      setIsAddingToLibrary(false);
      console.error("Unexpected error saving files:", error);
    }
  };

  const enableAddToLibrary = () => {
    // Count receipts that are "Ready to Save"
    const readyToSaveFiles = uploadedFiles.filter(
      (file) =>
        file.order &&
        file.order.status?.name === "Ready to Save" &&
        !file.processing
    );

    // Count files with issues (still processing or not ready)
    const filesWithIssues = uploadedFiles.filter(
      (file) =>
        file.processing ||
        !file.order ||
        file.order.status?.name !== "Ready to Save"
    );

    // Return true only if we have at least one ready file AND no files with issues
    return readyToSaveFiles.length > 0 && filesWithIssues.length === 0;
  };

  const handleFormChange = (id: string, updatedValues: any) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.map((item) => {
        if (item.id === id && item.order) {
          // Check if required fields are filled
          const hasRequiredFields =
            updatedValues.purchase_date && updatedValues.amount;

          // Update the order with the new values
          return {
            ...item,
            order: {
              ...item.order,
              name: updatedValues.name,
              purchase_date: updatedValues.purchase_date,
              amount: updatedValues.amount,
              beneficiary: updatedValues.beneficiary,
              notes: updatedValues.notes,
              // Update status based on required fields
              status: {
                name: hasRequiredFields ? "Ready to Save" : "Needs Details",
              },
            },
          };
        }
        return item;
      })
    );
  };

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>Receipt Upload</h1>
            <p>View and manage your existing receipts.</p>
          </div>
          <ActionButton
            icon={<PiPlus />}
            text="Upload Receipt"
            onClick={handleUpload}
            responsive={true}
          />
        </div>
        {/* <DismissableBanner
          header="What is HSA eligible?"
          subheader="Learn which expenses qualify for HSA reimbursement or let Shoebox's AI determine that for you."
          onClick={() => console.log("clicked")}
          onDismiss={() => console.log("dismissed")}
        /> */}
        <div className="upload-receipt-upload-area">
          <UploadArea
            showTitle={false}
            onFilesSelected={handleFilesFromUploadComponent}
          />
        </div>
        <div className="page-header">
          <div className="page-header-title">
            <h2>Uploaded Receipts</h2>
            <p>
              {uploadedFiles.length > 0
                ? `${uploadedFiles.length} receipt${
                    uploadedFiles.length !== 1 ? "s" : ""
                  } ready for review`
                : "Upload a receipt to get started."}
            </p>
          </div>
          <ActionButton
            icon={
              isAddingToLibrary ? (
                <PiSpinnerGap className="spin-animation" />
              ) : (
                <PiPlus />
              )
            }
            text={`Add to Library`}
            onClick={handleAddToLibrary}
            responsive={false}
            disabled={!enableAddToLibrary() || isAddingToLibrary}
          />
        </div>
        <div className="upload-receipt-section">
          {uploadedFiles.map((item) => (
            <EditReceiptForm
              key={item.id}
              order={item.order}
              receiptUrl={item.previewUrl}
              fileName={item.fileName}
              detailView={false}
              onDelete={() => handleRemoveFile(item.id)}
              onFormChange={handleFormChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReceiptUpload;
